import React, { useEffect, useState } from 'react';
import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import FinalPayable from './final_payable';
import SessionDrilldownModal from './session_drilldown_modal';
import ShadowBanner from './shadow_banner';
import WhyPopover from './why_popover';
import { fetch_l2 } from '../api';
import { fmt, needs_review_reason } from '../format';
import { NEEDS_REVIEW_TEXT, l2_view } from './adapters';
import { DRILLDOWN_KIND } from './params';
import { audit_link } from '../audit/params';

/*
 * PROD-7828 R5 — Overview Level 2: one teacher's breakdown for the pay month.
 * Four category rows (with Waivers, D-3/R5 brief), incentives, additional-payment entries
 * (R8 brief §5.4), final payable (D-7) and the recalc computation history.
 * Read-only throughout: R5 writes nothing (D-1).
 */
const SUB_TABS = [
  { key: 'summary', label: 'Payment summary' },
  { key: 'incentives', label: 'Incentives & additions' },
  { key: 'final', label: 'Final payable' },
  { key: 'history', label: 'Computation history' }
];

const OverviewLevel2 = ({ pay_month, month_label, teacher, on_back, on_error }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [sub_tab, setSubTab] = useState('summary');
  const [drill, setDrill] = useState(null);

  const teacher_uuid = teacher && teacher.teacher_uuid;

  useEffect(() => {
    if (!teacher_uuid || !pay_month) return undefined;
    let cancelled = false;
    setLoading(true);
    fetch_l2({ teacher_uuid, pay_month })
      .then((res) => {
        if (cancelled) return;
        setData(l2_view(res));
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        on_error(e, 'Failed to load the teacher payment breakdown.');
        setData(null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [teacher_uuid, pay_month, on_error]);

  const back_button = (
    <button type="button" className="tp-btn-out" style={{ marginBottom: 14 }} onClick={on_back}>
      ← Back to all teachers
    </button>
  );

  if (loading) {
    return (
      <div>
        {back_button}
        <CustomLoading />
      </div>
    );
  }
  if (!data) {
    return (
      <div>
        {back_button}
        <div className="tp-empty">No breakdown available for this teacher and month.</div>
      </div>
    );
  }

  const { meta, summary, incentives, additional, history } = data;
  const stale = data.needs_review;

  const open_drill = (row, kind) =>
    setDrill({
      category: row.category,
      category_label: kind === DRILLDOWN_KIND.PENALTIES ? `${row.label} — penalties` : row.label,
      kind
    });

  const num_cell = (value, tone) => <span className={tone}>{fmt(value, 2)}</span>;

  // R6 (CD-205/CD-206): the unattributed line joins the table as one more row so DataTable's
  // table/card dual render (concierge_v3 pattern) covers it too, instead of a hand-rolled <tr>.
  const summary_rows = summary.unattributed
    ? [...summary.rows, { ...summary.unattributed, is_unattributed: true }]
    : summary.rows;

  const penalty_drill_target = (r) =>
    r.is_unattributed ? { category: r.category, label: 'Not linked to a session type' } : r;

  const summary_columns = [
    {
      key: 'category',
      label: 'Category',
      hideInCard: true,
      render: (r) =>
        r.is_unattributed ? (
          <span
            className="tp-strong"
            title="Manual or month-level penalties, and rows computed before penalties carried a session type"
          >
            Penalties not linked to a session type
          </span>
        ) : (
          <span className="tp-strong">{r.label}</span>
        )
    },
    {
      key: 'sessions_count',
      label: 'Sessions',
      render: (r) => {
        if (r.is_unattributed) return '—';
        return r.can_drill_items ? (
          <button
            type="button"
            className="tp-link"
            onClick={() => open_drill(r, DRILLDOWN_KIND.ALL)}
          >
            {r.sessions_count}
          </button>
        ) : (
          r.sessions_count
        );
      }
    },
    {
      key: 'amount',
      label: 'Amount',
      align: 'right',
      render: (r) => (r.is_unattributed ? '—' : num_cell(r.amount, r.amount_tone))
    },
    {
      key: 'penalty_amount',
      label: 'Penalty',
      align: 'right',
      render: (r) =>
        r.can_drill_penalties ? (
          <button
            type="button"
            className="tp-link tp-neg"
            onClick={() => open_drill(penalty_drill_target(r), DRILLDOWN_KIND.PENALTIES)}
          >
            {fmt(r.penalty_amount, 2)}
          </button>
        ) : (
          num_cell(r.penalty_amount, 'tp-neg')
        )
    },
    {
      key: 'waived_amount',
      label: 'Waivers',
      align: 'right',
      render: (r) => num_cell(r.waived_amount, 'tp-pos')
    },
    {
      key: 'correction_amount',
      label: 'Corrections',
      align: 'right',
      render: (r) => num_cell(r.correction_amount, r.correction_amount < 0 ? 'tp-neg' : 'tp-pos')
    },
    {
      key: 'free_pass_amount',
      label: 'Free pass',
      align: 'right',
      render: (r) => num_cell(r.free_pass_amount, 'tp-pos')
    },
    {
      key: 'uncharged_orphan_amount',
      label: 'Uncharged orphans',
      align: 'right',
      render: (r) => num_cell(r.uncharged_orphan_amount, 'tp-neg')
    },
    {
      key: 'net_amount',
      label: 'Net',
      align: 'right',
      render: (r) =>
        r.is_unattributed ? '—' : <span className="tp-strong">{fmt(r.net_amount, 2)}</span>
    }
  ];

  return (
    <div>
      {back_button}
      <a className="tp-link" style={{ marginLeft: 12 }} href={audit_link(pay_month, teacher_uuid)}>
        All activity this month
      </a>

      <ShadowBanner show={data.shadow_banner} />

      <div className="tp-meta-grid">
        <div className="tp-meta-field">
          <div className="tp-meta-k">Teacher</div>
          <div className="tp-meta-v">
            {meta.teacher_name || (teacher && teacher.teacher_name) || '--'}
          </div>
          <div className="tp-card-field-label">{meta.teacher_email || ''}</div>
          <div className="tp-card-field-label tp-mono">{meta.teacher_uuid || teacher_uuid}</div>
        </div>
        <div className="tp-meta-field">
          <div className="tp-meta-k">Month</div>
          <div className="tp-meta-v">{month_label}</div>
        </div>
        <div className="tp-meta-field">
          <div className="tp-meta-k">Base rate (month snapshot)</div>
          <div className="tp-meta-v">{fmt(meta.base_rate_snapshot)}</div>
        </div>
        <div className="tp-meta-field">
          <div className="tp-meta-k">GST</div>
          <div className="tp-meta-v">
            {meta.is_gst_registered ? 'Registered' : 'Not registered'}
          </div>
        </div>
        <div className="tp-meta-field">
          <div className="tp-meta-k">Addl-participant type</div>
          <div className="tp-meta-v">
            {meta.addl_participant_teacher_type || <span className="tp-neg">⚠ untagged</span>}
          </div>
        </div>
        <div className="tp-meta-field">
          <div className="tp-meta-k">Free Pass balance</div>
          <div className="tp-meta-v">
            {meta.free_pass_balance === null || meta.free_pass_balance === undefined
              ? '--'
              : meta.free_pass_balance}
          </div>
        </div>
        <div className="tp-meta-field">
          <div className="tp-meta-k">Net payable (rounded)</div>
          <div className="tp-meta-v" style={{ color: 'var(--brand-primary)' }}>
            {stale ? (
              <span className="tp-neg">{NEEDS_REVIEW_TEXT}</span>
            ) : (
              fmt(data.rounded_net_payable)
            )}
          </div>
        </div>
      </div>

      {stale ? (
        <div className="tp-banner tp-banner-error">
          ⚠ {needs_review_reason(meta)} — the amounts below are stale until the month recomputes.
        </div>
      ) : null}

      <div className="tp-tabs" style={{ marginBottom: 14 }}>
        {SUB_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`tp-tab ${sub_tab === t.key ? 'on' : ''}`}
            onClick={() => setSubTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {sub_tab === 'summary' ? (
        <div className="tp-card">
          <div className="tp-card-hd">
            <span className="tp-card-hd-title">Payment summary</span>
            <span className="tp-card-hd-sub">
              Click a session count or penalty for session-level detail
            </span>
          </div>
          <DataTable
            columns={summary_columns}
            rows={summary_rows}
            rowKey={(r) => r.category}
            emptyText="No payment items for this month."
            cardTitle={(r) =>
              r.is_unattributed ? 'Penalties not linked to a session type' : r.label
            }
          />
        </div>
      ) : null}

      {sub_tab === 'incentives' ? (
        <>
          <div className="tp-card">
            <div className="tp-card-hd">
              <span className="tp-card-hd-title">System-calculated incentives</span>
            </div>
            <div className="tp-tablewrap">
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Type</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th>Related session</th>
                  </tr>
                </thead>
                <tbody>
                  {incentives.map((i) => (
                    <tr key={i.key}>
                      <td>{i.category}</td>
                      <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{i.item_type}</td>
                      <td style={{ textAlign: 'right' }}>
                        <WhyPopover
                          detail={i.explain}
                          amount_node={<span className="tp-pos">{i.amount_text}</span>}
                        />
                      </td>
                      <td>
                        {i.session_uuid ? (
                          <a
                            className="tp-link"
                            href={`/sessiondetails?id=${i.session_uuid}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Session
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                  {incentives.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="tp-empty">
                        No incentives for this month.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
          <div className="tp-card">
            <div className="tp-card-hd">
              <span className="tp-card-hd-title">Additional payments</span>
              <span className="tp-card-hd-sub">
                Inside gross, before GST and TDS (PRD 22.4) · entered on the Additional payments tab
              </span>
            </div>
            <div className="tp-tablewrap">
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Date / time (IST)</th>
                    <th>Category</th>
                    <th>Ref</th>
                    <th>Source</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th>Why</th>
                    <th>Note</th>
                    <th>Added by</th>
                  </tr>
                </thead>
                <tbody>
                  {additional.map((a) => (
                    <tr key={a.key}>
                      <td>{a.type_label}</td>
                      <td style={{ fontSize: 11 }}>{a.date_text}</td>
                      <td>{a.category_label}</td>
                      <td className="tp-mono">{a.reference_id || '—'}</td>
                      <td>{a.source_text}</td>
                      <td style={{ textAlign: 'right' }}>
                        <span className={a.amount_tone}>{a.amount_text}</span>
                      </td>
                      <td>
                        {a.explain && a.explain.rule_ref ? (
                          <WhyPopover
                            detail={a.explain}
                            label="Why this entry?"
                            amount_node={<span>{a.explain.rule_ref}</span>}
                          />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--text-muted)' }} title={a.note}>
                        {a.note || '—'}
                      </td>
                      <td style={{ fontSize: 11 }} title={a.added_by_title || undefined}>
                        {a.added_by}
                      </td>
                    </tr>
                  ))}
                  {additional.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="tp-empty">
                        No additional payments for this month.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}

      {sub_tab === 'final' ? (
        <FinalPayable lines={data.final_payable} gst_registered={!!meta.is_gst_registered} />
      ) : null}

      {sub_tab === 'history' ? (
        <div className="tp-card">
          <div className="tp-card-hd">
            <span className="tp-card-hd-title">Computation history</span>
            <span className="tp-card-hd-sub">
              Recalc audit rows — every change to this month is explainable
            </span>
          </div>
          <div className="tp-tablewrap">
            <table className="tp-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Trigger</th>
                  <th>Actor</th>
                  <th>Reason</th>
                  {history.show_net ? <th style={{ textAlign: 'right' }}>Net</th> : null}
                </tr>
              </thead>
              <tbody>
                {history.rows.map((h) => (
                  <tr key={h.key}>
                    <td style={{ fontSize: 11 }}>{h.when}</td>
                    <td>{h.action}</td>
                    <td>{h.actor}</td>
                    <td style={{ fontSize: 11 }}>{h.reason}</td>
                    {history.show_net ? (
                      <td style={{ textAlign: 'right' }}>{h.net_text || '—'}</td>
                    ) : null}
                  </tr>
                ))}
                {history.rows.length === 0 ? (
                  <tr>
                    <td colSpan={history.show_net ? 5 : 4} className="tp-empty">
                      No recomputations recorded for this month.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <SessionDrilldownModal
        show={!!drill}
        close={() => setDrill(null)}
        pay_month={pay_month}
        month_label={month_label}
        teacher_uuid={teacher_uuid}
        category={drill ? drill.category : ''}
        category_label={drill ? drill.category_label : ''}
        kind={drill ? drill.kind : ''}
        on_error={on_error}
      />

      {/* Penalty editing, waivers and Free Pass actions live on the Penalties tab (R6). */}
      <div className="tp-card-hd-sub" style={{ marginTop: 10, display: 'block' }}>
        Penalty edits, waivers and email sends are not part of R5 — this page is read-only.
      </div>
    </div>
  );
};

export default OverviewLevel2;
